import { AIR } from '@vm/shared';
import { M } from '../palette';
import { MapBuilder, speckle, yawToward, type Brush } from './builder';
import { stairKeepClear, boundaryBand, bandScatter, bunting, DistanceField, doorsFor, freeGround, G, house, lantern, pieces, Region, scatter, warpColumns, type Box, type Footprint, type Piece, regionPlay } from './kit';
import { emptyMeta, type MapDef, type MapMeta } from './types';

/**
 * Gondola Peak: a snowy mountain-top cable-car station, in the layout of a famous mountain-station arena map (see docs/maps/reference-maps.md):
 * a long, narrow plan with a big station hall in the middle, a lift station and a pump house to the north, a power block and a ski hut to the
 * north-east, a gear shed, warming hut and storage to the south, a rock-ringed trail round the west side and a round viewing platform at the
 * north-east. The plan is the reference's (36 x 76 m). Unlike the other hand-built maps this one has real ground height: the ground rises
 * 3.5 m towards the north end (the cable-car terminus) and 2.3 m towards the south end, so the station hall sits in a shallow bowl between the
 * two team yards. Buildings are built flat and the whole world is then warped (kit.warpColumns); buildings sit on flattened pads. Only the hall,
 * on level ground, has more than one floor with stairs, apart from the power block. Everything you see is ours: dark-timber cabins and a grey
 * station hall with orange trim under snow, prayer flags, pines, snowmen, a lattice pylon and a red gondola on its cable.
 * Not mirrored: spawns are placed by hand and checked with tools/map-audit.ts.
 */
const SX = 704;
const SY = 224;
const SZ = 1056;
const OX = 171; // play-area origin (voxels)
const OZ = 148;
const px = (x: number): number => OX + Math.round(x * 10);
const pz = (z: number): number => OZ + Math.round(z * 10);
const wx = (x: number): number => OX / 10 + x; // play metres -> world metres
const wz = (z: number): number => OZ / 10 + z;
const WALL_H = 58;
const BAND = 4.5; // metres of cliff wall outside the outline
const SHOULDER = 7; // metres over which the raised ground fades out beyond the wall (a tight shoulder keeps the out-of-play scenery - and its triangles - small)

const OUTLINE: [number, number][] = [
  [17.1, 0.0], [16.1, 1.3], [13.0, 1.6], [12.7, 7.6], [10.9, 8.1], [10.9, 11.8], [8.2, 12.1], [6.6, 13.7], [6.6, 16.9], [8.5, 18.3], [7.9, 23.2], [6.9, 23.9],
  [6.7, 25.8], [7.4, 26.5], [6.2, 27.4], [5.4, 26.9], [3.9, 27.0], [1.3, 30.1], [1.7, 35.8], [0.1, 38.2], [0.0, 42.2], [1.9, 45.0], [1.8, 48.0], [3.6, 51.3],
  [5.1, 52.9], [8.7, 54.8], [8.8, 58.9], [12.5, 59.3], [12.5, 67.0], [15.3, 67.5], [15.4, 73.8], [17.4, 76.0], [28.4, 76.0], [31.1, 72.2], [27.4, 68.0],
  [27.1, 66.1], [25.4, 62.9], [26.2, 62.1], [26.5, 60.7], [31.5, 60.5], [31.5, 52.1], [31.9, 51.6], [35.3, 51.3], [35.1, 41.3], [36.1, 40.6], [36.2, 38.1],
  [34.9, 37.6], [34.8, 36.6], [33.2, 35.0], [33.2, 29.8], [31.6, 29.1], [33.0, 26.7], [31.8, 24.1], [34.3, 22.4], [33.3, 19.1], [33.7, 14.5], [32.8, 12.9],
  [34.7, 11.5], [35.1, 8.8], [33.6, 6.6], [31.8, 6.0], [30.1, 6.5], [28.5, 7.8], [26.9, 7.8], [26.6, 1.5], [25.0, 0.0],
];
const region = new Region(OUTLINE);

/** Douglas-Peucker: the outline with vertices whose deviation from the kept curve is under `tol` voxels removed. The visible wall is built on this
 *  simplified line - the raw outline jogs every metre, and every jog splits the wall's faces into one-column strips, which alone cost more triangles
 *  than the whole playable map. The play-area mask and the building-footprint checks use the same simplified region, so wall, warp and mask agree. */
function simplifyOutline(pts: [number, number][], tol: number): [number, number][] {
  if (pts.length <= 2) return pts.slice();
  const keep = new Array<boolean>(pts.length).fill(false);
  keep[0] = keep[pts.length - 1] = true;
  const stack: [number, number][] = [[0, pts.length - 1]];
  const d2 = (x0: number, z0: number, x1: number, z1: number, x: number, z: number): number => {
    const dx = x1 - x0; const dz = z1 - z0;
    const L2 = dx * dx + dz * dz || 1;
    const t = Math.max(0, Math.min(1, ((x - x0) * dx + (z - z0) * dz) / L2));
    return (x0 + t * dx - x) ** 2 + (z0 + t * dz - z) ** 2;
  };
  while (stack.length) {
    const [a, b] = stack.pop()!;
    let worst = -1; let wd = 0;
    for (let i = a + 1; i < b; i++) { const d = d2(pts[a][0], pts[a][1], pts[b][0], pts[b][1], pts[i][0], pts[i][1]); if (d > wd) { wd = d; worst = i; } }
    if (wd > tol * tol && worst > 0) {
      keep[worst] = true;
      stack.push([a, worst], [worst, b]);
    }
  }
  return pts.filter((_, i) => keep[i]);
}
// Metres -> voxels, simplified for the band; the footprint check below still validates every building against it.
const bandRegion = new Region(simplifyOutline(OUTLINE.map(([x, z]) => [x * 10, z * 10] as [number, number]), 22).map(([x, z]) => [x / 10, z / 10] as [number, number]));

// Palette ---------------------------------------------------------------------------------------
// No transparent material (ice) in a surface speckle: transparent faces cannot merge with the opaque ones beside them, and one member
// in five interleaved breaks every merge run on the whole snowfield.
const SNOW: Brush = speckle([M('snow_1'), M('snow_2'), M('snow_1'), M('snow_1'), M('snow_2')], 61);
const APRON: Brush = speckle([M('concrete_2'), M('concrete_4'), M('snow_1'), M('concrete_2')], 63);
// snow_1 dropped: it belongs to the snowfield's family, and a shared member splits the wall faces.
const CLIFF: Brush = speckle([M('rock_1'), M('rock_2'), M('stone_4'), M('rock_2'), M('rock_1')], 65);
const ORANGE = M('paint_orange');
const FLAGS = [M('paint_red'), M('paint_yellow'), M('paint_blue'), M('paint_green'), M('paint_white')];

// Buildings: footprints in play metres from the reference plan -----------------------------------------------------------
interface Bld extends Footprint {
  name: string;
  floors: number;
  wall: number;
  trim: number;
  roof: number;
  roofStyle: 'flat' | 'gable' | 'gableX';
  kind: 'hall' | 'shed' | 'cabin' | 'power' | 'store';
  floor: number;
  roofAccess?: boolean;
}
const BUILDINGS: Bld[] = [
  { name: 'hall', x0: 11.0, z0: 33.8, x1: 31.0, z1: 47.2, floors: 2, wall: M('hull_gray'), trim: ORANGE, roof: M('paint_white'), roofStyle: 'flat', kind: 'hall', floor: M('concrete_2'), roofAccess: true },
  { name: 'pump', x0: 15.5, z0: 3.6, x1: 24.5, z1: 9.6, floors: 1, wall: M('siding_ochre'), trim: M('paint_white'), roof: M('snow_2'), roofStyle: 'gable', kind: 'shed', floor: M('planks_2') },
  { name: 'lift', x0: 12.5, z0: 17.5, x1: 19.5, z1: 26.0, floors: 1, wall: M('hull_white'), trim: M('paint_teal'), roof: M('paint_white'), roofStyle: 'flat', kind: 'shed', floor: M('concrete_2') },
  { name: 'skihut', x0: 24.5, z0: 16.8, x1: 31.0, z1: 20.4, floors: 1, wall: M('planks_dark_1'), trim: M('paint_white'), roof: M('snow_2'), roofStyle: 'gable', kind: 'cabin', floor: M('planks_2') },
  { name: 'power', x0: 24.8, z0: 21.4, x1: 30.5, z1: 29.6, floors: 2, wall: M('plate'), trim: ORANGE, roof: M('paint_white'), roofStyle: 'flat', kind: 'power', floor: M('concrete_4') },
  { name: 'gear', x0: 26.8, z0: 49.0, x1: 30.6, z1: 53.0, floors: 1, wall: M('siding_barn'), trim: M('paint_white'), roof: M('snow_2'), roofStyle: 'gable', kind: 'cabin', floor: M('planks_2') },
  { name: 'warming', x0: 10.2, z0: 52.6, x1: 14.4, z1: 57.6, floors: 1, wall: M('planks_dark_1'), trim: M('paint_white'), roof: M('snow_2'), roofStyle: 'gable', kind: 'cabin', floor: M('planks_2') },
  { name: 'store', x0: 24.6, z0: 55.6, x1: 30.4, z1: 59.4, floors: 1, wall: M('hull_gray'), trim: ORANGE, roof: M('paint_white'), roofStyle: 'flat', kind: 'store', floor: M('concrete_2') },
  { name: 'shelter', x0: 14.8, z0: 62.0, x1: 20.4, z1: 65.6, floors: 1, wall: M('siding_sage'), trim: M('paint_white'), roof: M('snow_2'), roofStyle: 'gable', kind: 'cabin', floor: M('planks_2') },
];

/** Island of rock in the west trail: players walk round it (the trail is the narrow strip between it and the west cliff). */
const ISLAND: [number, number][] = [[4.0, 40.5], [5.4, 37.0], [9.0, 35.6], [10.2, 38.0], [10.2, 50.0], [9.4, 51.4], [7.6, 49.4], [4.8, 46.2], [3.9, 43.0]];
const island = new Region(ISLAND);

// Ground height -------------------------------------------------------------------------------------------------------------
/** Height of the ground above the plateau (voxels) from the position along the plan: up to the north end and up to the south end. */
const ramp = (zm: number): number => (zm < 28 ? Math.min(36, (28 - zm) * 1.15) : zm > 60 ? Math.min(36, (zm - 60) * 1.45) : 0);

/**
 * Column shift in voxels at voxel column (X, Z): the ramp, flattened to a constant under every building and faded out beyond the wall. The ramp is
 * zero over the whole middle of the plan, so only the two ends need the per-building pad test: columns there are looked up through one distance
 * field instead of looping over every outline edge and every building for each of the 750,000 columns of the world.
 */
function makeHeightShift(field: DistanceField): (X: number, Z: number) => number {
  // A building needs a pad if the slope reaches its 2.5 m pad zone anywhere (the ramp grows away from z 28..60).
  const padded = BUILDINGS.filter((b) => ramp(b.z0 - 2.5) > 0 || ramp(b.z1 + 2.5) > 0 || ramp((b.z0 + b.z1) / 2) > 0);
  return (X, Z) => {
    const xm = (X - OX + 0.5) / 10;
    const zm = (Z - OZ + 0.5) / 10;
    if (xm < -20 || xm > 56 || zm < -20 || zm > 96) return 0;
    const raw = ramp(zm);
    if (raw === 0) return 0;
    let fade = 1;
    if (!field.inside(X, Z)) {
      const d = field.distance(X, Z);
      fade = d <= BAND ? 1 : d > BAND + SHOULDER ? 0 : Math.max(0, 1 - (d - BAND) / SHOULDER);
    }
    let v = raw * fade;
    if (fade === 1 && raw > 0) {
      let best = 2.5;
      let pad: Bld | null = null;
      for (const b of padded) {
        const dx = Math.max(b.x0 - xm, 0, xm - b.x1);
        const dz = Math.max(b.z0 - zm, 0, zm - b.z1);
        if (dx > best || dz > best) continue;
        const d = Math.sqrt(dx * dx + dz * dz);
        if (d < best) {
          best = d;
          pad = b;
        }
      }
      if (pad) {
        const t = best / 2.5;
        const s = t * t * (3 - 2 * t);
        const c = ramp((pad.z0 + pad.z1) / 2);
        v = c + (raw - c) * s;
      }
    }
    // Inside, the trail rises in 0.2 m steps (two voxels: within the 0.55 m auto-step, so walking is unchanged) so its ground merges in two-column runs.
    if (field.inside(X, Z)) return Math.round(v / 4) * 4;
    // Outside the play area the mountains terrace in 0.8 m steps that depend on z alone (the outline's north and south edges are
    // near-straight, so rows of equal height run the full width): broad flat runs merge into a handful of quads instead of a
    // 1-voxel step face at every column boundary. A distance-field fade here would wiggle per column and defeat the merging.
    // Inside, the shift is exact as before.
    if (!field.inside(X, Z)) {
      const beyond = Math.max(0, 1 - zm, zm - 75);
      const zFade = beyond > SHOULDER ? 0 : 1 - beyond / SHOULDER;
      v = Math.round((raw * zFade) / 8) * 8;
    }
    return Math.round(v);
  };
}

const place = (x: number, z: number, r: number, fn: () => void): void => {
  if (region.inside(x, z) && region.edgeDistance(x, z) > r && !island.inside(x, z)) fn();
};

function furnish(b: MapBuilder, bd: Bld, box: Box, seed: number): void {
  const boxOf = box;
  for (let f = 0; f < bd.floors; f++) {
    const y = G + 30 * f;
    const room = { x0: box.x0 + 2, z0: box.z0 + 2, x1: box.x1 - 2, z1: box.z1 - 2, y };
    const avoid = stairKeepClear(boxOf, f);
    const lots: Piece[] = [];
    const up = f > 0;
    switch (bd.kind) {
      case 'hall':
        if (!up) lots.push(pieces.counter(b, y, 60, 7), pieces.sofa(b, y, M('cloth_red')), pieces.sofa(b, y, M('cloth_blue')), pieces.table(b, y, 24, 12), pieces.shelfX(b, y, 28, 22), pieces.crate(b, y, 10), pieces.crate(b, y, 8), pieces.barrel(b, y, M('paint_orange')), pieces.table(b, y, 20, 12), pieces.shelfZ(b, y, 24, 22));
        else lots.push(pieces.table(b, y, 28, 12), pieces.counter(b, y, 50, 7), pieces.shelfX(b, y, 26, 22), pieces.sofa(b, y, M('cloth_green')), pieces.crate(b, y, 10), pieces.barrel(b, y, M('paint_teal')), pieces.shelfZ(b, y, 26, 22));
        break;
      case 'power':
        if (!up) lots.push(pieces.crate(b, y, 10), pieces.barrel(b, y, M('paint_yellow')), pieces.crate(b, y, 8), pieces.shelfX(b, y, 22, 22), pieces.barrel(b, y, M('paint_orange')));
        else lots.push(pieces.crate(b, y, 10), pieces.barrel(b, y, M('paint_teal')), pieces.table(b, y, 16, 10), pieces.shelfX(b, y, 20, 20));
        break;
      case 'cabin':
        lots.push(pieces.sofa(b, y, M('cloth_red')), pieces.table(b, y, 16, 10), pieces.shelfX(b, y, 18, 20), pieces.counter(b, y, 28, 7), pieces.barrel(b, y, M('paint_brown')));
        break;
      case 'store':
        lots.push(pieces.crate(b, y, 10), pieces.crate(b, y, 8), pieces.barrel(b, y, M('paint_orange')), pieces.shelfX(b, y, 26, 22), pieces.crate(b, y, 10), pieces.shelfX(b, y, 24, 22));
        break;
      case 'shed':
        lots.push(pieces.crate(b, y, 10), pieces.barrel(b, y, M('paint_green')), pieces.shelfX(b, y, 22, 22), pieces.table(b, y, 16, 10), pieces.crate(b, y, 8));
        break;
    }
    scatter(b, room, lots, seed + f * 3, avoid);
  }
}

function snowman(b: MapBuilder, x: number, z: number): void {
  const snow = M('snow_1');
  b.sphere(x, G + 5, z, 6, snow);
  b.sphere(x, G + 14, z, 4.6, snow);
  b.sphere(x, G + 21, z, 3.6, snow);
  b.box(x - 1, G + 21, z + 3, x + 1, G + 22, z + 7, M('paint_orange'));
  b.box(x - 2, G + 22, z + 3, x - 1, G + 23, z + 4, M('paint_black'));
  b.box(x + 1, G + 22, z + 3, x + 2, G + 23, z + 4, M('paint_black'));
  b.box(x - 4, G + 24, z - 4, x + 5, G + 25, z + 5, M('paint_black'));
  b.box(x - 2, G + 25, z - 2, x + 3, G + 29, z + 3, M('paint_black'));
  b.box(x - 5, G + 16, z - 1, x - 3, G + 19, z + 1, M('log_2'));
  b.box(x + 4, G + 16, z - 1, x + 6, G + 19, z + 1, M('log_2'));
}

function snowRock(b: MapBuilder, x: number, z: number, r: number): void {
  b.rock(x, G, z, r, M('stone_4'));
  b.blob(x, G + (r >> 1) + 3, z, r * 0.8, r * 0.35, r * 0.8, M('snow_1'), 0.2, true);
}

function build(b: MapBuilder): MapMeta {
  // Flat build: ground, wall, buildings, props ---------------------------------------------------------------------------------
  // The deep layer is one material (it is nearly all buried): letting it speckle rock_1/rock_2 would claim those shades for an invisible
  // family and split the visible cliff wall's speckle instead.
  // Sub speckle keeps one family (stone_4 belongs to the cliff's): a mixed family id splits terrace sides into one-tall fragments.
  b.ground(G, SNOW, speckle([M('dirt_1'), M('dirt_3')], 62), M('rock_1'));
  const bandField = new DistanceField(bandRegion, OX, OZ, BAND + 0.01);
  boundaryBand(b, bandRegion, { ox: OX, oz: OZ, band: BAND, height: WALL_H, skirt: M('snow_1'), skirtH: 5, face: CLIFF, core: M('rock_1'), cap: M('snow_1') }, bandField);
  const bad: string[] = [];
  for (const bd of BUILDINGS)
    for (const [x, z] of [[bd.x0, bd.z0], [bd.x1, bd.z0], [bd.x0, bd.z1], [bd.x1, bd.z1]])
      if (!bandRegion.inside(x, z) || bandRegion.edgeDistance(x, z) < 0.15 || island.inside(x, z)) bad.push(`${bd.name} (${x}, ${z})`);
  if (bad.length) throw new Error(`gondola_peak: corners outside or on the boundary: ${bad.join('; ')}`);
  for (const a of BUILDINGS) for (const c of BUILDINGS) if (a.name < c.name && a.x0 < c.x1 + 0.3 && a.x1 > c.x0 - 0.3 && a.z0 < c.z1 + 0.3 && a.z1 > c.z0 - 0.3) throw new Error(`gondola_peak: ${a.name} and ${c.name} touch`);

  // The concrete apron round the hall, and the packed-snow trail.
  for (let X = px(0); X < px(37); X++)
    for (let Z = pz(30.5); Z < pz(51.5); Z++) {
      const xm = (X - OX + 0.5) / 10;
      const zm = (Z - OZ + 0.5) / 10;
      if (!region.inside(xm, zm) || island.inside(xm, zm)) continue;
      if (xm > 9.0 && xm < 33.5) b.set(X, G - 1, Z, ((X >> 2) + (Z >> 2)) % 3 === 0 ? M('concrete_4') : M('concrete_2'));
    }

  const boxes = new Map<string, Box>();
  for (const bd of BUILDINGS) {
    const box = house(b, {
      x0: px(bd.x0), z0: pz(bd.z0), x1: px(bd.x1), z1: pz(bd.z1), floors: bd.floors, wall: bd.wall, roof: bd.roof, roofStyle: bd.roofStyle,
      doors: doorsFor(bd, BUILDINGS, region, 'gondola_peak'), post: bd.trim, trim: bd.trim, plinth: M('stone_4'), floor: bd.floor,
      windows: { w: 10, h: 12, sill: 8, spacing: 24 }, roofAccess: bd.roofAccess, railing: bd.roofAccess ? 24 : undefined, parapet: 4,
    });
    boxes.set(bd.name, box);
  }
  let seed = 4;
  for (const bd of BUILDINGS) furnish(b, bd, boxes.get(bd.name)!, (seed += 7));
  // Orange band round the hall and the power block at the floor line.
  for (const n of ['hall', 'power']) {
    const bx = boxes.get(n)!;
    b.walls(bx.x0 - 1, G + 28, bx.z0 - 1, bx.x1 + 1, G + 30, bx.z1 + 1, 1, ORANGE);
  }
  // Radio mast on the power block and a dish on the hall roof.
  {
    const bx = boxes.get('power')!;
    const cx = (bx.x0 + bx.x1) >> 1;
    const cz = (bx.z0 + bx.z1) >> 1;
    b.box(cx, bx.top, cz, cx + 2, bx.top + 54, cz + 2, M('steel_dark_1'));
    b.box(cx - 6, bx.top + 40, cz, cx + 8, bx.top + 41, cz + 2, M('steel_dark_1'));
    b.box(cx - 5, bx.top + 30, cz, cx + 7, bx.top + 31, cz + 2, M('steel_dark_1'));
    b.box(cx, bx.top + 54, cz, cx + 2, bx.top + 58, cz + 2, M('paint_red'));
  }
  {
    const bx = boxes.get('hall')!;
    const cx = bx.x1 - 40;
    const cz = bx.z0 + 26;
    b.cylinder(cx, cz, bx.top + 2, bx.top + 14, 2, M('steel_dark_1'));
    b.dome(cx, bx.top + 14, cz, 9, M('paint_white'));
    b.clear(cx - 6, bx.top + 16, cz - 6, cx + 7, bx.top + 24, cz + 7);
    b.dome(cx, bx.top + 14, cz, 9, M('paint_white'), 8);
    b.box(cx - 1, bx.top + 14, cz - 1, cx + 1, bx.top + 26, cz + 1, M('steel_dark_1'));
  }

  // Yards, trail and outcrops ------------------------------------------------------------------------------------------------------
  // The rock island in the west trail (solid; its top is snowy).
  for (let X = px(2); X < px(11); X++)
    for (let Z = pz(34); Z < pz(52); Z++) {
      const xm = (X - OX + 0.5) / 10;
      const zm = (Z - OZ + 0.5) / 10;
      if (!island.inside(xm, zm)) continue;
      const h = 22 + ((X * 7 + Z * 13) % 5);
      b.boxIfAir(X, G, Z, X + 1, G + h - 1, Z + 1, CLIFF);
      b.boxIfAir(X, G + h - 1, Z, X + 1, G + h, Z + 1, M('snow_1'));
    }
  for (const [x, z, r] of [[27.0, 31.4, 7], [10.0, 31.5, 8], [20.5, 29.3, 6], [33.0, 33.0, 6], [3.5, 33.5, 8], [12.0, 62.5, 7], [24.5, 68.0, 8], [30.0, 55.0, 6], [20.0, 55.5, 7], [14.0, 14.5, 8], [5.5, 29.5, 7]] as [number, number, number][])
    place(x, z, 1.2 + r / 20, () => snowRock(b, px(x), pz(z), r));
  // Pines: a few in the yards and along the edges.
  for (const [x, z, h] of [[12.0, 10.0, 60], [28.0, 5.0, 64], [9.5, 28.0, 52], [32.0, 46.0, 58], [14.0, 70.0, 62], [28.0, 62.0, 54], [8.0, 57.0, 50], [33.0, 17.0, 56], [24.0, 62.0, 50], [11.0, 20.0, 56]] as [number, number, number][])
    place(x, z, 3.0, () => {
      if (freeGround(b, px(x) - 3, pz(z) - 3, px(x) + 4, pz(z) + 4)) b.pineTree(px(x), G, pz(z), h, true, M('leaves_3'));
    });
  // Snowmen, lantern posts and crate stacks as cover.
  place(23.0, 15.8, 1.5, () => snowman(b, px(23.0), pz(15.8)));
  place(19.0, 66.8, 1.5, () => snowman(b, px(19.0), pz(66.8)));
  for (const [x, z] of [[16.0, 30.2], [24.5, 31.0], [33.0, 48.0], [22.0, 51.5], [12.0, 60.5], [26.0, 12.0], [18.0, 58.0], [22.5, 63.0], [3.0, 50.0], [2.4, 39.0]] as [number, number][])
    place(x, z, 1.0, () => lantern(b, px(x), pz(z), 34));
  for (const [x, z] of [[27.2, 14.4], [14.4, 16.2], [30.0, 40.0], [17.0, 50.0], [26.0, 59.4], [20.0, 60.0], [9.5, 58.0]] as [number, number][])
    place(x, z, 1.0, () => {
      if (freeGround(b, px(x), pz(z), px(x) + 10, pz(z) + 10)) {
        b.crate(px(x), G, pz(z), 10);
        if ((x * 3 + z) % 2 < 1) b.crate(px(x) + 12, G, pz(z), 8);
      }
    });
  // Orange safety fence along the north yard's edge of the slope, and prayer flags across the north yard and the south yard.
  place(20.0, 12.0, 1.0, () => bunting(b, px(12.0), G + 48, pz(11.2), px(26.0), G + 48, pz(11.2), FLAGS, 6));
  place(20.0, 12.0, 1.0, () => bunting(b, px(16.0), G + 40, pz(70.0), px(24.0), G + 40, pz(70.0), FLAGS, 5));
  b.box(px(12.0), G + 46, pz(11.2), px(12.0) + 2, G + 49, pz(11.2) + 2, M('log_2'));

  // Ground height: raise the whole world by the shift, then place everything that needs the real surface ------------------------------------
  const shiftField = new DistanceField(bandRegion, OX, OZ, BAND + SHOULDER + 0.01);
  warpColumns(b, makeHeightShift(shiftField), 156);

  // The cable car: a lattice pylon on the viewing platform, a second pylon outside the north wall, two cables and a red gondola between ------
  {
    const ax = px(31.4);
    const az = pz(9.6);
    const ay = b.surfaceY(ax, az);
    b.cylinder(ax, az, ay - 4, ay + 6, 9, M('concrete_2'));
    b.lattice(ax - 4, ay + 6, az - 4, ax + 4, ay + 96, az + 4, 'y', M('steel_dark_1'), 2, 12);
    b.box(ax - 12, ay + 96, az - 3, ax + 13, ay + 98, az + 3, M('steel_dark_2'));
    const bx = px(31.4);
    const bz = pz(-10.5);
    const by = b.surfaceY(bx, bz);
    b.lattice(bx - 4, by, bz - 4, bx + 4, by + 110, bz + 4, 'y', M('steel_dark_1'), 2, 12);
    b.box(bx - 12, by + 110, bz - 3, bx + 13, by + 112, bz + 3, M('steel_dark_2'));
    for (const dx of [-9, 10]) {
      b.line(ax + dx, ay + 97, az, bx + dx, by + 111, bz, M('steel_dark_1'), 1);
    }
    // Gondola hanging from the cables a bit more than halfway to the far pylon.
    const t = 0.34;
    const gx = Math.round(ax + (bx - ax) * t);
    const gz = Math.round(az + (bz - az) * t);
    const gy = Math.round(ay + 97 + (by + 111 - (ay + 97)) * t);
    b.box(gx - 9, gy - 6, gz - 1, gx + 11, gy - 4, gz + 1, M('steel_dark_1'));
    b.box(gx - 7, gy - 24, gz - 7, gx + 9, gy - 8, gz + 7, M('paint_red'));
    b.box(gx - 7, gy - 24, gz - 7, gx + 9, gy - 22, gz + 7, M('paint_white'));
    b.box(gx - 7, gy - 17, gz - 8, gx + 9, gy - 11, gz - 7, M('glass_tint'));
    b.box(gx - 7, gy - 17, gz + 7, gx + 9, gy - 11, gz + 8, M('glass_tint'));
    b.box(gx - 8, gy - 17, gz - 7, gx - 7, gy - 11, gz + 7, M('glass_tint'));
    b.box(gx + 9, gy - 17, gz - 7, gx + 10, gy - 11, gz + 7, M('glass_tint'));
    b.box(gx - 1, gy - 8, gz - 1, gx + 3, gy - 6, gz + 1, M('steel_dark_1'));
  }
  // A forest outside the wall, on the shoulders of the mountain: dense where players see it along the wall, sparse beyond. (A tree also
  // splits the merged snowfield quads around it, so a thick forest everywhere costs hundreds of thousands of triangles of pure scenery.)
  const pine = (X: number, Z: number): void => {
    const y = b.surfaceY(X, Z);
    if (y > 4) b.blockyPine(X, y, Z, 46 + ((X * 3 + Z * 5) % 26), true, M('leaves_3'));
  };
  bandScatter(bandRegion, OX, OZ, BAND + 1.5, BAND + 6, 400, 71, SX, SZ, pine);
  bandScatter(bandRegion, OX, OZ, BAND + 6, BAND + 14, 2200, 73, SX, SZ, pine);

  // Metadata ----------------------------------------------------------------------------------------------------------------------------------
  const gy = G / 10;
  const meta = emptyMeta(gy, wx(18.0), wz(38.0));
  const mid: [number, number] = [wx(20.5), wz(40.5)];
  const at = (x: number, z: number): number => b.standAt(wx(x), wz(z), 9.0);
  const t0: [number, number][] = [[14.4, 12.0], [16.4, 12.0], [18.4, 12.0], [20.4, 12.0], [15.4, 14.2], [17.4, 14.2], [19.4, 14.2], [21.4, 14.2]];
  const t1: [number, number][] = [[17.6, 71.4], [19.8, 71.4], [22.0, 71.4], [24.2, 71.4], [18.8, 73.4], [21.0, 73.4], [23.2, 73.4], [25.4, 73.4]];
  for (const [x, z] of t0) meta.spawns.push({ team: 0, x: wx(x), y: at(x, z), z: wz(z), yaw: yawToward(wx(x), wz(z), mid[0], mid[1]) });
  for (const [x, z] of t1) meta.spawns.push({ team: 1, x: wx(x), y: at(x, z), z: wz(z), yaw: yawToward(wx(x), wz(z), mid[0], mid[1]) });
  const ffa: [number, number][] = [
    [20.0, 31.0], [12.0, 30.5], [28.0, 31.8], [2.2, 41.0], [3.3, 47.0], [33.4, 40.5], [33.4, 44.8], [16.0, 29.4], [9.8, 21.0], [14.0, 50.2], [20.0, 50.5],
    [21.0, 59.5], [14.5, 60.8], [19.0, 56.0], [28.0, 14.5], [22.0, 19.5],
  ];
  for (const [x, z] of ffa) meta.spawns.push({ team: -1, x: wx(x), y: at(x, z), z: wz(z), yaw: yawToward(wx(x), wz(z), mid[0], mid[1]) });
  meta.points.push({ id: 'A', name: 'North Landing', x: wx(19.0), y: at(19.0, 12.8), z: wz(12.8), r: 3.2 });
  meta.points.push({ id: 'B', name: 'Station Hall', x: wx(20.5), y: at(20.5, 40.5), z: wz(40.5), r: 3.0 });
  meta.points.push({ id: 'C', name: 'South Landing', x: wx(21.0), y: at(21.0, 68.0), z: wz(68.0), r: 3.2 });
  meta.flags.push({ team: 0, x: wx(28.5), y: at(28.5, 11.0), z: wz(11.0) });
  meta.flags.push({ team: 1, x: wx(23.0), y: at(23.0, 64.0), z: wz(64.0) });
  const picks: [string, number, number][] = [
    ['health', 16.5, 29.5], ['health', 24.5, 32.0], ['armor', 33.2, 42.5], ['health', 2.2, 44.0], ['health', 21.0, 51.0], ['armor', 18.0, 53.0], ['health', 20.5, 59.0],
    ['health', 21.5, 18.5], ['armor', 10.3, 22.0],
  ];
  for (const [kind, x, z] of picks) meta.pickups.push({ kind: kind as 'health' | 'armor', x: wx(x), y: at(x, z), z: wz(z) });

  // Keep the voxels around every spawn clear and make sure there is floor under it.
  for (const s of meta.spawns) {
    const x = Math.floor(s.x * 10);
    const z = Math.floor(s.z * 10);
    const y = Math.round(s.y * 10);
    b.clear(x - 3, y, z - 3, x + 4, y + 18, z + 4);
    if (b.w.get(x, y - 1, z) === AIR) b.set(x, y - 1, z, M('stone_1'));
  }
  // Nothing that moves can leave the boundary: an invisible wall at every height along it (the boundary wall is the visible one).
  regionPlay(b, bandRegion, OX, OZ, BUILDINGS);
  return meta;
}

export const gondolaPeak: MapDef = {
  id: 'gondola_peak',
  name: 'Gondola Peak',
  description: 'A snowy mountain-top cable-car station: a big hall, a lift house and cabins on a slope, with a red gondola on its cable.',
  size: [SX, SY, SZ],
  modes: ['tdm', 'ffa', 'dom', 'ctf', 'sandbox', 'practice'],
  players: [4, 12],
  seed: 5105,
  version: 4,
  theme: {
    skyTop: 0x5a96e0,
    skyHorizon: 0xe8f2ff,
    fog: 0xdce8f6,
    fogNear: 45,
    fogFar: 170,
    sun: [0.5, 0.7, 0.35],
    sunColor: 0xfff4e0,
    sunIntensity: 1.3,
    ambient: 0xe4eeff,
    ambientIntensity: 1.1,
    outside: 0xdfe8f2,
    music: 'snow',
  },
  build,
};
